/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { LoggingWindow } from "./LoggingWindow";
import { WhinyConfigStore } from "./WhinyConfigStore";
import { WhinyStateProvider } from "./WhinyStateProvider";

/**
 * Applier facade tying together window parsing, runtime state, and
 * persistence for the Whiny logging window.
 *
 * - {@link applyFromRawWindow} runs after every successful device-features
 *   fetch: it parses the raw window, publishes it to the state provider,
 *   and persists it.
 * - {@link restoreFromStorage} runs at startup, before the first fetch,
 *   so a persisted window is honoured from the first request.
 */
export class WhinyLogging {
  /**
   * Applies the raw `whinyLogging` window value from a device-features
   * response. A missing or malformed value disables Whiny and clears the
   * persisted window.
   *
   * @param rawWindow - Raw `"<startEpochSec>-<endEpochSec>"` value, or null
   */
  public static async applyFromRawWindow(
    rawWindow: string | null
  ): Promise<void> {
    const window: LoggingWindow | null = LoggingWindow.parse(rawWindow);
    WhinyStateProvider.setWindow(window);
    await WhinyConfigStore.saveWindow(window);
  }

  /**
   * Restores the last persisted window into the state provider. Because
   * the state provider re-evaluates against wall-clock, an expired or
   * not-yet-started window correctly reads as off after restore.
   */
  public static async restoreFromStorage(): Promise<void> {
    const window: LoggingWindow | null = await WhinyConfigStore.loadWindow();
    WhinyStateProvider.setWindow(window);
  }
}
